import type { FC, PropsWithChildren } from 'react'

import { useState, useEffect } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import useMatchMedia from '@segment/utils/useMatchMedia'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

type CardData = {
  heading?: string
  author?: string
  link?: string
}

const data: CardData[] = [
  {
    heading: `How Twilio Segment proactively protects customer’s API tokens`,
    author: `Sal Olivares, Senior Software Engineer, Security`,
    link: `/blog/how-segment-proactively-protects-customer-api-tokens/`,
  },
  {
    heading: `Changing of the guard: migrating an authorization service used by thousands of customers from Go to Typescript`,
    author: `Rex Chen, Staff Software Engineer, Security`,
    link: `/blog/migrate-from-go-to-typescript/`,
  },
  {
    heading: `Tracking Meaningful Security Product Metrics`,
    author: `Leif Dreizler, Senior Engineering Manager, Security`,
    link: `/blog/track-meaningful-security-product-metrics/`,
  },
  {
    heading: `How to plan an SMS MFA migration that affects thousands of users`,
    author: `Jordan Kohl, Staff Software Engineer, Security`,
    link: `/blog/plan-sms-mfa-migration-for-thousands-users/`,
  },
  {
    heading: `Redefining Threat Modeling: Security team goes on vacation`,
    author: `Jeevan Singh, Director of Product Security`,
    link: `/blog/redefining-threat-modeling/`,
  },
  {
    heading: `Access Service: Temporary Access to the Cloud`,
    author: `Andy Li, Software Engineer, Security`,
    link: `/blog/access-service/`,
  },
  {
    heading: `Shifting Engineering Right: What security engineers can learn from DevSecOps`,
    author: `Leif Dreizler, Senior Engineering Manager, Security`,
    link: `/blog/shifting-engineering-right/`,
  },
  {
    heading: `How we approach security`,
    author: `Coleen Coolidge, CISO at Twilio Segment`,
    link: `/blog/how-we-approach-security/`,
  },
  {
    heading: `Scaling security services with AWS organizations`,
    author: `Pablo Vidal Bouza, Cloud Security Engineer`,
    link: `/blog/segment-aws-organizations/`,
  },
]

const copy = {
  heading: `Articles by Security`,
}

const Articles: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const theme = useTheme()
  const desktopResolutionMediaQuery = theme.breakpoints.up(`lg`, false)
  const twoColumnMediaQuery = theme.breakpoints.down(`lg`, false)
  const isDesktopResolution = useMatchMedia(desktopResolutionMediaQuery, true)
  const isTwoColumnResolution = useMatchMedia(twoColumnMediaQuery, false)
  const [limit, setLimit] = useState(9)

  useEffect(() => {
    setLimit(isDesktopResolution ? 9 : 3)
  }, [isDesktopResolution, isTwoColumnResolution])

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading>{copy.heading}</CopyText.Heading>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <ColumnTrack columns={1} overrides={{ md: 2, xl: 3 }}>
            {data.slice(0, limit).map((data, index) =>
              data.heading ? (
                <CardBase key={index} href={getAbsoluteUrl(data.link || ``, pageConfig)}>
                  <Card.Body>
                    <Card.Copy>
                      <Card.Headline>{data.heading}</Card.Headline>
                      <Card.Description variant="pMedium">{data.author}</Card.Description>
                    </Card.Copy>
                    <Card.Action alignment="right">
                      <ChevronRightIcon />
                    </Card.Action>
                  </Card.Body>
                </CardBase>
              ) : (
                isDesktopResolution && !isTwoColumnResolution && <div key={index}></div>
              )
            )}
          </ColumnTrack>
        </Section.Contents>
        {limit <= data.length && limit !== data.length && (
          <StyledButtonText onClick={() => setLimit(data.length)} iconEnd={ChevronRightIcon}>
            Show all
          </StyledButtonText>
        )}
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 60px;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      row-gap: 80px;
    }
  }
`

const StyledButtonText = styled(ButtonText)`
  margin: 0 auto;
`

export default Articles
